import './styles.scss';
// Registers all AG Grid modules needed by the Rules, RuleSets and TagSpecs grids.
// This import is cached by the JS module system — registration happens only once.
import 'Core-utils/helpers/rulesAgGridModules';
import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from 'react';
import type { GridApi } from 'ag-grid-community';
import { Typography } from 'Components/atoms';
import { AgGridBase, useClientExternalTextSearch } from 'Components/organisms/AgGridBase';
import { Button } from 'react-bootstrap';
import { ConfirmationModal } from 'Components/molecules';
import { listRules, deleteRuleById, listRuleSets } from 'Apis/library';
import {
    YES_DELETE_IT,
    DEFAULT_ERROR_MSG,
    DELETE_RULE_MODAL_CONTENT,
    DELETE_RULE_MODAL_TITLE,
    RULES_SEARCH_PLACEHOLDER,
    RULES_CREATE_BTN,
    RULES_REFRESH_BTN,
    RULES_RULE_DELETED,
    RULES_TAB_RULES,
    RULES_TAB_TAGSPECS,
    RULES_TAB_RULESETS,
} from 'Core-utils/constants/constants';
import { useI18n } from 'Hooks/useI18n';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { TagSpecsConfig } from 'Components/organisms/TagSpecsConfig';
import { RuleSetsConfig } from 'Components/organisms/RuleSetsConfig';
import { ViewConditionsModal } from 'Components/organisms/ViewConditionsModal';
import { RuleDto, RuleOption } from 'Core-utils/types/rules.types';
import { RuleSetDto } from 'Core-utils/types/RuleSetsConfig.types';
import { getRulesGridColumns } from './getRulesGridColumns';
import type { ICellRendererParams } from 'ag-grid-enterprise';

// ─── Tab identifiers ──────────────────────────────────────────────────────────
const RULE_CONFIG_TABS = {
    RULES: 'rules',
    TAG_SPECS: 'tagspecs',
    RULE_SETS: 'rulespecs',
} as const;

type RuleConfigTab = typeof RULE_CONFIG_TABS[keyof typeof RULE_CONFIG_TABS];

// ─── Stable getRowId (defined outside component to avoid recreation per render) ──
const getRowId = (params: { data?: RuleDto }) => String(params.data?.id ?? '');

// ─── Detail cell renderer (raw JSON expand row) ───────────────────────────────
const DetailCellRenderer = ({ data }: ICellRendererParams<RuleDto>) => (
    <div className="rules-expandable-row">
        <JSONPretty data={data} />
    </div>
);

// ─── RulesConfig ──────────────────────────────────────────────────────────────
interface RulesConfigProps {
    refresh?: boolean;
    onEditRule?: (rule: RuleDto | null) => void;
}

const RulesConfig = ({ refresh, onEditRule }: RulesConfigProps) => {
    const { t } = useI18n();
    const { addToast } = useContext(ToastsContext);
    const tid = useSelector((state: RootState) => state.auth.selectedTenant.id) as string | undefined;
    const { extractedData: connectorsList } = useConnectorData();

    // ── Active tab ────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState<RuleConfigTab>(RULE_CONFIG_TABS.RULES);

    // ── Internal refresh toggle — flipping it changes the SWR cache key which
    //    triggers a re-fetch. Do NOT call refresh() alongside this; that would
    //    fire a duplicate request. ────────────────────────────────────────────
    const [internalRefresh, setInternalRefresh] = useState(false);

    // ── Rules list ────────────────────────────────────────────────────────────
    // useApiData (not useApiObjectData) — defaults to [] when no data; correct for lists.
    const {
        data: rulesList,
        isLoading: isRulesLoading,
    } = useApiObjectData<{ data: unknown }, RuleDto[]>(
        tid ? `rules-${internalRefresh}-${refresh}` : '',
        listRules,
        (res) => {
            const body = (res as { data?: unknown }).data;
            if (Array.isArray(body)) {
                return body as RuleDto[];
            }
            const nested = (body as { data?: unknown })?.data;
            if (Array.isArray(nested)) {
                return nested as RuleDto[];
            }
            return [];
        },
        { tid, limit: 200 },
        !!tid,
    );

    // ── Rule Sets list (shared by Rules grid + Rule Sets tab) ────────────────
    // Single SWR cache so tab switches never remount with a stale rulesets key.
    const {
        data: ruleSetsList,
        isLoading: isRuleSetsLoading,
        refresh: revalidateRuleSets,
    } = useApiObjectData<{ data: unknown }, RuleSetDto[]>(
        tid ? `rulesets-${tid}` : '',
        listRuleSets,
        (res) => {
            const body = (res as { data?: unknown }).data;
            if (Array.isArray(body)) {
                return body as RuleSetDto[];
            }
            const nested = (body as { data?: unknown })?.data;
            if (Array.isArray(nested)) {
                return nested as RuleSetDto[];
            }
            return [];
        },
        { tid, limit: 200 },
        !!tid,
    );

    const ruleSetsArray = useMemo(
        () => (Array.isArray(ruleSetsList) ? ruleSetsList : []),
        [ruleSetsList],
    );

    // Optimistic list shown until SWR revalidation completes after a mutation.
    const [optimisticRuleSets, setOptimisticRuleSets] = useState<RuleSetDto[] | null>(null);

    useEffect(() => {
        if (!isRuleSetsLoading) {
            setOptimisticRuleSets(null);
        }
    }, [ruleSetsList, isRuleSetsLoading]);

    const effectiveRuleSets = optimisticRuleSets ?? ruleSetsArray;

    const handleRuleSetMutated = useCallback(
        (updated?: RuleSetDto) => {
            if (updated) {
                const base = optimisticRuleSets ?? ruleSetsArray;
                const idx = base.findIndex((rs) => rs.id === updated.id);
                if (idx >= 0) {
                    const next = [...base];
                    next[idx] = { ...next[idx], ...updated };
                    setOptimisticRuleSets(next);
                } else {
                    setOptimisticRuleSets([updated, ...base]);
                }
            }
            void revalidateRuleSets();
        },
        [optimisticRuleSets, ruleSetsArray, revalidateRuleSets],
    );

    // ── Connector map (cid → display name) ────────────────────────────────────
    const connectorMap = useMemo(() => {
        const map: Record<string, string> = {};
        (connectorsList || []).forEach((c) => {
            map[c.cid] = c.title || c.cid;
        });
        return map;
    }, [connectorsList]);

    // ── Rule Set map (id → name) ──────────────────────────────────────────────
    const ruleSetMap = useMemo(() => {
        const map: Record<string, string> = {};
        effectiveRuleSets.forEach((rs: RuleSetDto) => {
            if (rs?.id) {
                map[rs.id] = rs.name ?? rs.id;
            }
        });
        return map;
    }, [effectiveRuleSets]);

    const rowData: RuleDto[] = useMemo(
        () => (Array.isArray(rulesList) ? rulesList : []),
        [rulesList],
    );

    // ── Rule options passed to TagSpecsConfig (derived here to avoid duplicate fetch) ─
    // Passes null while loading so TagSpecsConfig can show a loading state
    // without needing to know about the parent's isLoading flag.
    const ruleOptions: RuleOption[] | null = useMemo(
        () =>
            isRulesLoading
                ? null
                : rowData.map((r) => ({ id: r.id, displayValue: r.id, value: r.id, cid: r.cid })),
        [rowData, isRulesLoading],
    );

    // ── Refs for connector / ruleSet maps ─────────────────────────────────────
    // columnDefs are memoized with [t] so they never re-create when the maps change.
    // Refs give cell renderers a stable pointer that always reads the latest value
    // without triggering useMemo re-evaluation.
    const connectorMapRef = useRef(connectorMap);
    connectorMapRef.current = connectorMap;

    const ruleSetMapRef = useRef(ruleSetMap);
    ruleSetMapRef.current = ruleSetMap;

    const gridApiRef = useRef<GridApi<RuleDto> | null>(null);

    const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
        useClientExternalTextSearch<RuleDto>({
            fields: [
                (row) => row.id,
                (row) => connectorMapRef.current[row.cid] ?? row.cid,
                (row) => (row.rule_set_id ? ruleSetMapRef.current[row.rule_set_id] ?? row.rule_set_id : ''),
            ],
            onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
        });

    // ── View conditions modal ──────────────────────────────────────────────────
    const [viewConditionsRule, setViewConditionsRule] = useState<RuleDto | null>(null);

    // ── Delete ─────────────────────────────────────────────────────────────────
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);

    const handleDeleteClick = useCallback((id: string) => {
        setRuleToDelete(id);
        setShowDeleteModal(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!ruleToDelete) {
            return;
        }
        try {
            await deleteRuleById(ruleToDelete);
            addToast({ severity: 'success', body: t(RULES_RULE_DELETED, 'Rule deleted successfully') });
            setInternalRefresh((v) => !v);
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setShowDeleteModal(false);
            setRuleToDelete(null);
        }
    }, [ruleToDelete, addToast, t]);

    // ── Edit ───────────────────────────────────────────────────────────────────
    const handleEditClick = useCallback((row: RuleDto) => onEditRule?.(row), [onEditRule]);

    // ── Stable callback refs for column defs ──────────────────────────────────
    // columnDefs is memoized with [t] only. Using refs here prevents the cell
    // renderers from holding stale closures after re-renders while keeping the
    // column definitions stable.
    const handleDeleteClickRef = useRef(handleDeleteClick);
    handleDeleteClickRef.current = handleDeleteClick;

    const handleEditClickRef = useRef(handleEditClick);
    handleEditClickRef.current = handleEditClick;

    // ── Column definitions (extracted to getRulesGridColumns.tsx) ─────────────
    const columnDefs = useMemo(
        () =>
            getRulesGridColumns(
                t,
                connectorMapRef,
                ruleSetMapRef,
                handleEditClickRef,
                handleDeleteClickRef,
                setViewConditionsRule,
            ),
        [t],
    );

    return (
        <div className="d-flex flex-column h-100">
            {/* ── Tab bar ─────────────────────────────────────────────────── */}
            <div className="d-flex ps-0 pe-4 rules-config__tab-bar">
                {([RULE_CONFIG_TABS.RULES, RULE_CONFIG_TABS.TAG_SPECS, RULE_CONFIG_TABS.RULE_SETS] as const).map((tab) => (
                    <div
                        key={tab}
                        className={`me-4 rules-config__tab-item rules-config__tab-item--${activeTab === tab ? 'active' : 'inactive'}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        <Typography
                            variant="body2"
                            className="mb-0"
                            color={activeTab === tab ? 'text-high-emphasis' : 'text-low-emphasis'}
                        >
                            {tab === RULE_CONFIG_TABS.RULES
                                ? t(RULES_TAB_RULES, 'Rules')
                                : tab === RULE_CONFIG_TABS.TAG_SPECS
                                ? t(RULES_TAB_TAGSPECS, 'Tag Specs')
                                : t(RULES_TAB_RULESETS, 'Rule Sets')}
                        </Typography>
                    </div>
                ))}
            </div>

            <div className="flex-grow-1 rules-config__tab-content">
                {activeTab === RULE_CONFIG_TABS.RULES && (
                    <div className="d-flex flex-column h-100">
                        {/* ── Toolbar ──────────────────────────────────────── */}
                        <div className="d-flex align-items-center gap-3 ps-0 pe-4 rules-config__toolbar">
                            <input
                                type="text"
                                className="form-control form-control-sm rules-config__search-input"
                                placeholder={t(RULES_SEARCH_PLACEHOLDER, 'Search by rule id or connector…')}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <Button
                                size="sm"
                                className="d-flex align-items-center gap-2 text-white border-0 rules-config__create-btn"
                                onClick={() => onEditRule?.(null)}
                            >
                                {t(RULES_CREATE_BTN, '+ Create Rule')}
                            </Button>
                            <Button
                                className="d-flex align-items-center gap-2 ms-auto rules-config__refresh-btn"
                                size="sm"
                                onClick={() => setInternalRefresh((v) => !v)}
                                disabled={isRulesLoading}
                            >
                                {t(RULES_REFRESH_BTN, '↻ Refresh')}
                            </Button>
                        </div>

                        {/* ── Grid ─────────────────────────────────────────── */}
                        <div className="rules-config__table-wrapper">
                            <AgGridBase.Root fullWidth>
                                <AgGridBase.Header hidden />
                                <AgGridBase.Table<RuleDto>
                                    columnDefs={columnDefs}
                                    dataMode="client"
                                    rowData={rowData}
                                    showSidebar={false}
                                    movableColumns={false}
                                    wrapperClassName="rules-ag-grid__wrapper--theme-findings ag-findings-grid__wrapper ag-grid-theme--amp-dark"
                                    defaultColDef={{
                                        wrapHeaderText: false,
                                        autoHeaderHeight: false,
                                        minWidth: 80,
                                        cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                                    }}
                                    agGridProps={{
                                        loading: isRulesLoading,
                                        getRowId,
                                        isExternalFilterPresent,
                                        doesExternalFilterPass,
                                        onGridReady: (e) => { gridApiRef.current = e.api; },
                                        onGridPreDestroyed: () => { gridApiRef.current = null; },
                                        suppressContextMenu: true,
                                        animateRows: false,
                                        rowHeight: 72,
                                        masterDetail: true,
                                        isRowMaster: () => true,
                                        detailCellRenderer: DetailCellRenderer,
                                        detailRowAutoHeight: true,
                                    }}
                                />
                            </AgGridBase.Root>
                        </div>

                        <ConfirmationModal
                            modalTitle={DELETE_RULE_MODAL_TITLE}
                            modalBodyTitle={DELETE_RULE_MODAL_CONTENT}
                            onPrimaryButtonClick={confirmDelete}
                            primaryButtonLabel={t(YES_DELETE_IT)}
                            show={showDeleteModal}
                            onClose={() => {
                                setShowDeleteModal(false);
                                setRuleToDelete(null);
                            }}
                            buttonVariant="danger"
                        />
                    </div>
                )}

                {/* ViewConditionsModal is rendered outside the tab guard so it can
                    overlay correctly even if the tab switches while it is open. */}
                <ViewConditionsModal
                    rule={viewConditionsRule}
                    onClose={() => setViewConditionsRule(null)}
                />

                {activeTab === RULE_CONFIG_TABS.TAG_SPECS && (
                    <TagSpecsConfig
                        refresh={refresh}
                        ruleOptions={ruleOptions}
                        connectorMap={connectorMap}
                    />
                )}

                {activeTab === RULE_CONFIG_TABS.RULE_SETS && (
                    <RuleSetsConfig
                        connectorMap={connectorMap}
                        ruleSetsList={effectiveRuleSets}
                        isRuleSetsLoading={isRuleSetsLoading}
                        onRefreshRuleSets={() => handleRuleSetMutated()}
                        onRuleSetMutated={handleRuleSetMutated}
                    />
                )}
            </div>
        </div>
    );
};

export default RulesConfig;
